import { createContext, useContext } from 'react';

import { useHomeIntl } from '@/components/home-nova/shared/home-intl-context';
import type { HomeIntlPage } from '@/utils/home-intl/types';

/**
 * Onde o motion da Home liga: a lista de pares pelo caminho do par (`/br/pt`, o mesmo formato da lista das versões em
 * src/constants/home-intl/versoes.json), ou `'*'` para todos. As versões da Home de um par (/br/pt/v2...) têm o mesmo
 * `path` do par e herdam o motion. Fora da lista — as outras Homes de par e a Home dos 10 locales (sem par) — os blocos
 * renderizam exatamente o HTML de antes e nenhum pedaço de JS do motion é pedido.
 *
 * Fase 1 (16/10/2026): só a Home br/pt e as versões dela. Fase 2 (16/10/2026): as 195 Homes de par que passaram nas
 * provas (layout em 60 resoluções contra a l14, PageSpeed celular ≥ 95, RTL/CJK/índicas). Ficam de fora: as 20 Homes
 * legadas (ApprovedMainPage, sem os blocos da home-nova) e os 4 pares que respondem 404 (ao/umb, gt/quc, mx/nah, ye/gdq).
 */
export const HOME_MOTION_PARES: readonly string[] = [
  '/ae/ar',
  '/ae/en',
  '/ae/hi',
  '/ao/en',
  '/ao/pt',
  '/ar/en',
  '/ar/es',
  '/ar/pt',
  '/at/de',
  '/at/en',
  '/at/tr',
  '/au/ar',
  '/au/en',
  '/au/zh-hans',
  '/bd/bn',
  '/bd/en',
  '/bd/hi',
  '/be/de',
  '/be/fr',
  '/be/nl',
  '/bh/ar',
  '/bh/en',
  '/bh/hi',
  '/br/en',
  '/br/es',
  '/br/pt',
  '/by/en',
  '/by/ru',
  '/ca/en',
  '/ca/fr',
  '/ca/zh-hans',
  '/ch/de',
  '/ch/fr',
  '/ch/it',
  '/cl/en',
  '/cl/es',
  '/cn/en',
  '/cn/yue',
  '/cn/zh-hans',
  '/co/en',
  '/co/es',
  '/co/pt',
  '/cz/cs',
  '/cz/en',
  '/cz/sk',
  '/de/de',
  '/de/en',
  '/de/tr',
  '/dk/da',
  '/dk/de',
  '/dk/en',
  '/dz/ar',
  '/dz/en',
  '/dz/fr',
  '/ec/en',
  '/ec/es',
  '/eg/ar',
  '/eg/en',
  '/eg/fr',
  '/es/ca',
  '/es/en',
  '/es/es',
  '/fi/en',
  '/fi/fi',
  '/fi/sv',
  '/fr/ar',
  '/fr/en',
  '/fr/fr',
  '/gb-sct/en',
  '/gb-sct/pl',
  '/gb/en',
  '/gb/pl',
  '/gb/ro',
  '/gt/en',
  '/gt/es',
  '/hk/en',
  '/hk/yue',
  '/hk/zh-hans',
  '/id/en',
  '/id/id',
  '/ie/en',
  '/ie/pl',
  '/il/ar',
  '/il/en',
  '/il/he',
  '/in/en',
  '/in/hi',
  '/iq/ar',
  '/iq/en',
  '/ir/en',
  '/ir/fa',
  '/is/en',
  '/is/pl',
  '/it/de',
  '/it/en',
  '/it/it',
  '/jo/ar',
  '/jo/en',
  '/jo/fr',
  '/jp/en',
  '/jp/ja',
  '/jp/zh-hans',
  '/kr/en',
  '/kr/ko',
  '/kr/zh-hans',
  '/kw/ar',
  '/kw/en',
  '/kw/hi',
  '/kz/en',
  '/kz/kk',
  '/kz/ru',
  '/lb/ar',
  '/lb/en',
  '/lb/fr',
  '/ma/ar',
  '/ma/en',
  '/ma/fr',
  '/mn/en',
  '/mn/ru',
  '/mx/en',
  '/mx/es',
  '/my/en',
  '/my/ms',
  '/my/zh-hans',
  '/ng/en',
  '/nl/en',
  '/nl/nl',
  '/nl/tr',
  '/no/en',
  '/no/nb',
  '/nz/en',
  '/nz/zh-hans',
  '/om/ar',
  '/om/en',
  '/om/ur',
  '/pe/en',
  '/pe/es',
  '/ph/en',
  '/ph/fil',
  '/pk/en',
  '/pk/ur',
  '/pl/en',
  '/pl/pl',
  '/pl/uk',
  '/pt/en',
  '/pt/es',
  '/pt/pt',
  '/qa/ar',
  '/qa/en',
  '/qa/hi',
  '/ro/en',
  '/ro/hu',
  '/ro/ro',
  '/ru/en',
  '/ru/ru',
  '/sa/ar',
  '/sa/en',
  '/sa/ur',
  '/se/ar',
  '/se/en',
  '/se/sv',
  '/sg/en',
  '/sg/ms',
  '/sg/zh-hans',
  '/th/en',
  '/th/th',
  '/th/zh-hans',
  '/tn/ar',
  '/tn/en',
  '/tn/fr',
  '/tr/ar',
  '/tr/en',
  '/tr/tr',
  '/tw/en',
  '/tw/id',
  '/tw/zh-hant',
  '/tz/ar',
  '/tz/en',
  '/tz/sw',
  '/ua/en',
  '/ua/ru',
  '/ua/uk',
  '/us/en',
  '/us/es',
  '/us/zh-hans',
  '/uy/en',
  '/uy/es',
  '/uy/pt',
  '/vn/en',
  '/vn/ko',
  '/vn/vi',
  '/ye/ar',
  '/ye/en',
  '/za/af',
  '/za/en',
];

/** O motion da Home está ligado neste par? (`null` = Home dos 10 locales: nunca.) */
export function homeMotionAtivo(page: Pick<HomeIntlPage, 'path'> | null | undefined, pares: readonly string[] = HOME_MOTION_PARES): boolean {
  if (!page) return false;
  return pares.includes('*') || pares.includes(page.path);
}

/**
 * Motion ligado na Home dos 10 locales do site (sem par): o preview /preview/home/home-vN, no padrão do site, liga por aqui.
 * Atravessa as ilhas dos blocos pela ponte do lazy-hydrate.tsx.
 */
export const HomeMotionSiteContext = createContext(false);

/** Gancho dos blocos: o par da página está na lista (ou, sem par, a página ligou o motion)? Igual no servidor e no navegador. */
export function useHomeMotionAtivo(): boolean {
  const page = useHomeIntl();
  const site = useContext(HomeMotionSiteContext);
  return page ? homeMotionAtivo(page) : site;
}

/**
 * Atributo que só existe com o motion ligado (`data-m`, `data-motion-block`...): fora da lista vira `undefined` e o React
 * não escreve o atributo — o HTML dos outros pares fica byte a byte o de antes.
 */
export const marcaMotion =
  (ativo: boolean) =>
  (valor: string): string | undefined =>
    ativo ? valor : undefined;
